// ---------------------------------------------------------------------------
// The session guard and the sign-in's tab title (R13)
//
// app-root.ts puts every route that is not public behind a guard. Until R13
// that was Adornis's authenticationGuard over the BaseQL socket; now it is
// this one, over the REST session (session.ts):
//   - checking: a loading state (a Spinner, announced as "Loading SDR Agent"),
//     neither the page nor the sign-in. The first check starts here, so a
//     public page (opt-out, connected, the admin area) asks nothing about a
//     session: a prospect who unsubscribes sends no request to /api/auth/me;
//   - signed in: the page, through the outlet;
//   - signed out or unreachable: the sign-in page (pages/sign-in.ts), at the
//     address asked for; signed in, the page asked for opens there.
// The outlet stays a function the guard calls, so the page's remount (in the
// outlet) sits inside the guard, as app-root.ts requires.
//
// The tab title: page-change.ts writes the route's title on every page
// change, so over the sign-in the tab said the name of the page behind it
// ("Dashboard · SDR Agent by NDI", R12 plan, section 4). startSignInTitle
// writes "Sign in · SDR Agent by NDI" while the sign-in shows, with the
// deployment's prefix (🔴<STAGE/> on staging), also after a navigation, and
// the route's own title when it goes. Start it after startPageChange: its
// commit listener runs after page-change's.
// ---------------------------------------------------------------------------

import { effect } from '@preact/signals-core';
import { html, type TemplateResult } from 'lit';
import { deployment, titlePrefix } from '../deployment.js';
import { spinner } from '../design/feedback.js';
import '../pages/sign-in.js';
import { isPublicRoute } from '../public-routes.js';
import { APP_NAME, pageTitle } from '../router/page-change.js';
import type { Router } from '../router/router.js';
import type { RouteTable } from '../router/routes.js';
import type { Session, SessionState } from './session.js';

/** The page for a signed-in user, the sign-in for anyone else, a loading state until the session is known. */
export function sessionGuard(session: Session, outlet: () => TemplateResult): TemplateResult {
  const state = session.state.value;
  if (state.status === 'checking') {
    void session.check();
    return html`<div class="flex min-h-screen items-center justify-center" data-session="checking">
      ${spinner({ size: 'lg', label: 'Loading SDR Agent' })}
    </div>`;
  }
  if (state.status === 'signed-in') return outlet();
  return html`<sdr-sign-in-page
    .session=${session}
    .ended=${state.status === 'signed-out' && state.ended}
    .unreachable=${state.status === 'unreachable'}
  ></sdr-sign-in-page>`;
}

/** True while the guard shows the sign-in for this route. */
const showsSignIn = (route: string, state: SessionState) =>
  !isPublicRoute(route) && (state.status === 'signed-out' || state.status === 'unreachable');

export interface SignInTitleInput<Name extends string> {
  session: Session;
  router: Router<Name>;
  routes: RouteTable;
  /** The title prefix; the deployment's when left out. */
  prefix?: () => string;
}

/** Keeps the tab on "Sign in" while the sign-in shows; returns the function that stops it. */
export function startSignInTitle<Name extends string>({
  session,
  router,
  routes,
  prefix = () => titlePrefix(deployment()),
}: SignInTitleInput<Name>): () => void {
  let shown = false;
  const apply = () => {
    const route = router.state.peek().name;
    const now = showsSignIn(route, session.state.peek());
    if (now) document.title = `${prefix()}Sign in · ${APP_NAME}`;
    else if (shown) document.title = pageTitle(routes, route, prefix());
    shown = now;
  };
  // The session changes; the route is read without following it, the commit listener has that.
  const stopEffect = effect(() => {
    void session.state.value;
    apply();
  });
  const stopCommit = router.onCommit(() => apply());
  return () => {
    stopEffect();
    stopCommit();
  };
}
